import React from "react";
import { col, T, Tm, fmt } from "./kit";

const W = 600;

// ABC Pareto: share bars coloured by class with the cumulative line and the 70% / 90% cut-offs.
export function Pareto({ values }) {
  const v = [...values].sort((a, b) => b - a);
  const total = v.reduce((s, x) => s + x, 0);
  const H = 240, L = 40, R = 16, top = 16, bot = 34, ih = H - top - bot, bw = (W - L - R) / v.length;
  let cum = 0;
  const pts = [];
  const bars = v.map((x, i) => {
    const before = cum / total;
    cum += x;
    pts.push([L + bw * (i + 1), top + ih * (1 - cum / total)]);
    const cls = before < 0.7 ? "A" : before < 0.9 ? "B" : "C";
    return { x, i, cls };
  });
  const color = { A: col("primary"), B: col("accent"), C: col("subtle") };
  const y = (p) => top + ih * (1 - p);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[420px]" role="img" aria-label="Pareto chart of SKU value">
      {[0.7, 0.9].map(p => (
        <g key={p}><line x1={L} x2={W - R} y1={y(p)} y2={y(p)} stroke={col("warning", 0.8)} strokeDasharray="4 4" />
          <text x={W - R} y={y(p) - 4} textAnchor="end" style={{ ...Tm, fill: col("warning") }}>{p * 100}%</text></g>
      ))}
      {bars.map(b => (
        <g key={b.i}>
          <rect x={L + bw * b.i + 4} width={bw - 8} y={y(b.x / total)} height={ih * (b.x / total)} fill={color[b.cls]} opacity={0.85} rx={1} />
          <text x={L + bw * b.i + bw / 2} y={H - bot + 14} textAnchor="middle" style={Tm}>{b.i + 1}</text>
          <text x={L + bw * b.i + bw / 2} y={H - 6} textAnchor="middle" style={{ ...T, fontWeight: 600, fill: color[b.cls] }}>{b.cls}</text>
        </g>
      ))}
      <polyline points={[[L, y(0)], ...pts].map(p => p.join(",")).join(" ")} fill="none" stroke={col("fg")} strokeWidth={1.6} />
      {pts.map(([px, py], i) => <circle key={i} cx={px} cy={py} r={2.6} fill={col("fg")} />)}
      {[0, 0.5, 1].map(p => <text key={p} x={L - 6} y={y(p) + 4} textAnchor="end" style={Tm}>{p * 100}%</text>)}
    </svg>
  );
}

// Monthly / daily bar panels with optional mean line and outlier cap.
export function Bars({ panels }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-5">
      {panels.map((p, k) => <BarPanel key={k} p={p} w={panels.length === 1 ? 600 : 300} />)}
    </div>
  );
}

function BarPanel({ p, w }) {
  const H = 170, top = 14, bot = 18, ih = H - top - bot, n = p.values.length, bw = w / n;
  const max = Math.max(...p.values, p.cap || 0) * 1.08 || 1;
  const y = (v) => top + ih * (1 - v / max);
  const mean = p.values.reduce((s, v) => s + v, 0) / n;
  const tone = col(p.tone || "primary");
  return (
    <div>
      <div className="text-[12px] font-semibold mb-1.5">{p.title}</div>
      <svg viewBox={`0 0 ${w} ${H}`} className="w-full" role="img" aria-label={p.title}>
        <line x1={0} x2={w} y1={top + ih} y2={top + ih} stroke={col("border-strong")} />
        {p.values.map((v, i) => {
          const over = p.cap && v > p.cap;
          return (
            <g key={i}>
              <rect x={i * bw + 1.5} width={bw - 3} y={y(over ? p.cap : v)} height={Math.max(0, top + ih - y(over ? p.cap : v))} fill={tone} opacity={0.8} rx={1} />
              {over && <rect x={i * bw + 1.5} width={bw - 3} y={y(v)} height={y(p.cap) - y(v)} fill={col("critical", 0.18)} stroke={col("critical")} strokeDasharray="3 2" />}
            </g>
          );
        })}
        {p.mean && <><line x1={0} x2={w} y1={y(mean)} y2={y(mean)} stroke={col("fg", 0.7)} strokeDasharray="5 4" />
          <text x={w - 2} y={y(mean) - 4} textAnchor="end" style={{ ...Tm, fill: col("fg") }}>mean {fmt(mean, 1)}</text></>}
        {p.cap && <><line x1={0} x2={w} y1={y(p.cap)} y2={y(p.cap)} stroke={col("warning")} strokeWidth={1.5} />
          <text x={4} y={y(p.cap) - 4} style={{ ...Tm, fill: col("warning") }}>cap {p.cap}</text></>}
        <text x={0} y={H - 4} style={Tm}>1</text>
        <text x={w} y={H - 4} textAnchor="end" style={Tm}>{n}</text>
      </svg>
    </div>
  );
}

// Scatter with least-squares fit line and R².
export function Scatter({ points, x_label, y_label, r2, mark }) {
  const H = 250, L = 44, R = 16, top = 14, bot = 36;
  const xs = points.map(p => p[0]), ys = points.map(p => p[1]);
  const mx = xs.reduce((a, b) => a + b) / xs.length, my = ys.reduce((a, b) => a + b) / ys.length;
  const b = points.reduce((s, [x, yv]) => s + (x - mx) * (yv - my), 0) / points.reduce((s, [x]) => s + (x - mx) ** 2, 0);
  const a = my - b * mx;
  const xMax = Math.max(...xs, mark?.[0] || 0) * 1.1, yMax = Math.max(...ys, mark?.[1] || 0) * 1.12;
  const X = (v) => L + (W - L - R) * (v / xMax), Y = (v) => top + (H - top - bot) * (1 - v / yMax);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[420px]" role="img" aria-label="Demand against cooling degree days">
      <line x1={L} x2={W - R} y1={Y(0)} y2={Y(0)} stroke={col("border-strong")} />
      <line x1={L} x2={L} y1={top} y2={Y(0)} stroke={col("border-strong")} />
      <line x1={X(0)} y1={Y(a)} x2={X(xMax)} y2={Y(a + b * xMax)} stroke={col("primary")} strokeWidth={2} />
      {points.map(([x, yv], i) => <circle key={i} cx={X(x)} cy={Y(yv)} r={4.5} fill={col("accent", 0.85)} stroke={col("bg")} />)}
      {mark && <g><circle cx={X(mark[0])} cy={Y(mark[1])} r={7} fill="none" stroke={col("warning")} strokeWidth={2} />
        <text x={X(mark[0]) - 10} y={Y(mark[1]) - 12} textAnchor="end" style={{ ...Tm, fill: col("warning") }}>{mark[0]} CDD → {mark[1]} units</text></g>}
      <text x={L + 8} y={top + 12} style={{ ...Tm, fill: col("primary"), fontWeight: 600 }}>units ≈ {fmt(a, 1)} + {fmt(b, 2)} × CDD · R² {fmt(r2, 2)}</text>
      <text x={(L + W - R) / 2} y={H - 6} textAnchor="middle" style={T}>{x_label}</text>
      <text x={12} y={(top + H - bot) / 2} textAnchor="middle" transform={`rotate(-90 12 ${(top + H - bot) / 2})`} style={T}>{y_label}</text>
      {[0, Math.round(xMax / 2), Math.round(xMax / 1.1)].map(v => <text key={v} x={X(v)} y={Y(0) + 14} textAnchor="middle" style={Tm}>{v}</text>)}
    </svg>
  );
}

// Normal curve: the shaded area left of each class's z is its service level.
export function Bell({ levels }) {
  const H = 230, L = 20, R = 20, top = 16, bot = 40, xr = 3.4;
  const X = (z) => L + (W - L - R) * ((z + xr) / (2 * xr)), pdf = (z) => Math.exp(-z * z / 2);
  const Y = (v) => top + (H - top - bot) * (1 - v);
  const path = (to) => {
    const pts = [];
    for (let z = -xr; z <= to; z += 0.05) pts.push(`${X(z)},${Y(pdf(z))}`);
    return `M${X(-xr)},${Y(0)} L${pts.join(" L")} L${X(to)},${Y(0)} Z`;
  };
  const tones = ["subtle", "accent", "primary"];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[420px]" role="img" aria-label="Service level protection under the normal curve">
      {[...levels].sort((a, b) => b.z - a.z).map((l) => <path key={l.cls} d={path(l.z)} fill={col(tones[levels.indexOf(l)] || "primary", 0.22)} />)}
      <path d={path(xr)} fill="none" stroke={col("fg", 0.8)} strokeWidth={1.5} />
      <line x1={X(0)} x2={X(0)} y1={Y(1)} y2={Y(0)} stroke={col("fg", 0.4)} strokeDasharray="3 3" />
      <text x={X(0)} y={H - 22} textAnchor="middle" style={T}>average lead-time demand</text>
      {levels.map((l, i) => (
        <g key={l.cls}>
          <line x1={X(l.z)} x2={X(l.z)} y1={Y(0.62 - i * 0.16)} y2={Y(0)} stroke={col(tones[i] || "primary")} strokeWidth={2} />
          <text x={X(l.z) + 5} y={Y(0.62 - i * 0.16) + 4} style={{ ...Tm, fill: col(tones[i] === "subtle" ? "muted" : tones[i]), fontWeight: 600 }}>{l.cls} · {Math.round(l.p * 100)}% · z {l.z}</text>
        </g>
      ))}
      <text x={X(2.4)} y={H - 6} textAnchor="middle" style={T}>safety stock = z × σ →</text>
    </svg>
  );
}
